import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import {
  getServiceAppStatus,
  provisionServiceApp,
  type ServiceAppStatusResponse,
} from '@/lib/api/service-app';
import {
  Loader2,
  Database,
  FileText,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  Info,
} from 'lucide-react';

export default function ServiceAppSettings() {
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [status, setStatus] = useState<ServiceAppStatusResponse | null>(null);
  const { toast } = useToast();

  const loadStatus = useCallback(async () => {
    try {
      const data = await getServiceAppStatus();
      setStatus(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load service app status',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleProvision = async () => {
    setProvisioning(true);
    try {
      const result = await provisionServiceApp();
      if (result.status === 'already_configured') {
        toast({ title: 'Already configured', description: `Source: ${result.source}` });
      } else {
        toast({
          title: 'Service app provisioned',
          description: `client_id: ${result.client_id}`,
        });
      }
      await loadStatus();
    } catch (error: any) {
      toast({
        title: 'Provisioning failed',
        description: error.message || 'Failed to provision service app',
        variant: 'destructive',
      });
    } finally {
      setProvisioning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Okta Service App</h2>
        <p className="text-muted-foreground mt-1 max-w-3xl">
          The adapter authenticates to Okta's Management API for event-triggered operations
          (agent sync, key rotation) using this service app. It is auto-provisioned using the
          admin UI's own Okta token the first time an admin logs into an unconfigured deployment.
        </p>
      </div>

      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/30 border">
        <Info className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
        <div className="text-sm text-muted-foreground space-y-1">
          <p>
            The signing key never leaves the adapter — only the resulting Okta{' '}
            <code>client_id</code> is stored. The service app's <code>jwks_uri</code> points back
            at this adapter's own <code>/oauth/jwks.json</code>.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="py-4 px-6 space-y-4">
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div className="flex items-center gap-2">
              {status?.configured ? (
                <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              )}
              <span>{status?.configured ? 'Configured' : 'Not configured'}</span>
            </div>
            <div className="flex items-center gap-2">
              {status?.source === 'database' ? (
                <Database className="w-4 h-4 text-green-600 dark:text-green-400" />
              ) : (
                <FileText className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              )}
              <span>
                Source:{' '}
                {status?.source === 'database'
                  ? 'Database (auto-provisioned)'
                  : status?.source === 'env'
                  ? 'OKTA_SERVICE_CLIENT_ID env var'
                  : 'None'}
              </span>
            </div>
          </div>

          {status?.client_id && (
            <div className="text-sm space-y-1">
              <div>
                <span className="text-muted-foreground">Client ID: </span>
                <code>{status.client_id}</code>
              </div>
              {status.app_id && (
                <div>
                  <span className="text-muted-foreground">App ID: </span>
                  <code>{status.app_id}</code>
                </div>
              )}
              {status.jwks_uri && (
                <div>
                  <span className="text-muted-foreground">JWKS URI: </span>
                  <code>{status.jwks_uri}</code>
                </div>
              )}
              {status.provisioned_at && (
                <div className="text-muted-foreground">
                  Provisioned {status.provisioned_at} by {status.provisioned_by}
                </div>
              )}
            </div>
          )}

          {status?.requires_manual_key_upload && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900 dark:text-amber-200 space-y-1">
                <p>
                  <code>jwks_uri</code> was set to <code>{status.jwks_uri}</code>, but Okta
                  cannot reach a loopback host to fetch the public key. Either:
                </p>
                <ul className="list-disc ml-5 space-y-0.5">
                  <li>
                    Paste the current public key into the app's Client Credentials in the Okta
                    Admin Console — run{' '}
                    <code>curl {status.jwks_uri} | jq '.keys[0]'</code>, or
                  </li>
                  <li>
                    Point <code>GATEWAY_BASE_URL</code> at a publicly reachable HTTPS host and
                    re-provision.
                  </li>
                </ul>
              </div>
            </div>
          )}

          {!!status?.scopes_failed?.length && (
            <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-900 dark:text-amber-200 space-y-2">
                <div>
                  <p>These scopes could not be granted automatically and must be granted manually in the Okta Admin Console (Okta API Scopes tab):</p>
                  <ul className="list-disc ml-5">
                    {status.scopes_failed.map((scope) => (
                      <li key={scope}><code>{scope}</code></li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p>
                    The service app also needs an admin role assigned — Okta doesn't assign one
                    automatically. Recommended: <strong>AI Agent Administrator</strong> and{' '}
                    <strong>Application Administrator</strong> together, or a more restrictive
                    custom role covering the scopes above. <strong>Super Admin</strong> also works
                    but grants far more than this app needs.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!status?.configured && (
            <div className="flex justify-end">
              <Button onClick={handleProvision} disabled={provisioning}>
                {provisioning ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Provision now
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
